import { useDownload, useSnackbar } from '@e2m-customer-portal/hooks';
import { Box, ButtonBase, darken, SxProps, Theme } from '@mui/material';
import { FileDownloadIcon } from 'assets/icons';
import { MouseEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface DownloadItemProps {
  label: string;
  url?: string;
  filename?: string;
  onClick?: () => void;
  isHeader?: boolean;
  isGrey?: boolean;
}

export function DownloadItem({
  label,
  url,
  filename,
  onClick,
  isHeader,
  isGrey,
}: Readonly<DownloadItemProps>): ReactElement<DownloadItemProps> {
  const [t] = useTranslation();
  const { clickHandler } = useDownload();
  const [isLoading, setIsLoading] = useState(false);
  const { showError } = useSnackbar();

  const hasLink = !isHeader && (url != null || onClick != null);
  const disabled = (!isHeader && url == null && onClick == null) || isLoading;

  const handleClick =
    onClick ??
    (url != null
      ? (event: MouseEvent<HTMLAnchorElement>) => {
          setIsLoading(true);
          clickHandler()(event)
            .catch(() => showError(t('SHARED_ERROR_UNKNOWN')))
            .finally(() => setIsLoading(false));
        }
      : undefined);

  const content = (
    <>
      {!isHeader && (
        <Box
          sx={(theme) => ({
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '1px',
            marginRight: 1,
            border: `1px solid ${disabled ? theme.palette.grey[200] : theme.palette.primary.main}`,
            borderRadius: '4px',
            backgroundColor: theme.palette.common.white,
            '& svg': {
              fill: disabled ? theme.palette.grey[200] : theme.palette.primary.main,
            },
          })}
        >
          <FileDownloadIcon />
        </Box>
      )}
      {label}
    </>
  );

  const style: SxProps<Theme> = (theme) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    height: '40px',
    paddingLeft: 4,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    color: disabled ? theme.palette.grey[200] : theme.palette.common.black,
    backgroundColor: isGrey ? theme.palette.grey[100] : theme.palette.background.paper,
  });

  if (isHeader) {
    return <Box sx={[style, (theme) => theme.typography.body3]}>{content}</Box>;
  }

  return (
    <ButtonBase
      component={hasLink && url != null ? 'a' : 'div'}
      role={hasLink && url == null ? 'button' : undefined}
      disabled={disabled}
      aria-disabled={disabled}
      href={url}
      download={hasLink && url != null ? (filename ?? true) : undefined}
      onClick={handleClick}
      sx={[
        style,
        (theme) => ({
          cursor: hasLink ? 'pointer' : undefined,
          textDecoration: 'none',
          transition: theme.transitions.create('background-color'),
          '&:hover': hasLink
            ? {
                backgroundColor: darken(
                  isGrey ? theme.palette.grey[100] : theme.palette.background.paper,
                  theme.palette.action.hoverOpacity,
                ),
              }
            : undefined,
        }),
        (theme) => theme.typography.subtitle1,
      ]}
    >
      {content}
    </ButtonBase>
  );
}
